import { Transition } from "@headlessui/react";
import { Dispatch, SetStateAction, useContext, useState } from "react";
import { useModal, WorkflowContext } from "../contexts";
import WorkflowNoSource from "./WorkflowNoSource";
import WorkflowSourceFieldTable from "./WorkflowSourceFieldTable";
import { DocumentTextIcon, ExclamationIcon } from "@heroicons/react/outline";
import WorkflowSignatureConfigItem from "./WorkflowSignatureConfigItem";
import SmallSelectField from "./SmallSelectField";
import { DATE_FORMAT_MAP, TIME_FORMAT_MAP } from "../utils";
import WorkflowSelectModal from "./WorkflowSelectModal";
import TeamsLabel from "./TeamsLabel";
import SmallTextInputField from "./SmallTextInputField";
import ToggleButton from "./ToggleButton";
import ProLabel from "./ProLabel";
import { SignatureConfig } from "../interfaces";
import { useHistory, useRouteMatch } from "react-router-dom";
import PayWallModalPro from "./PayWallModalPro";
import useUser from "../hooks/useUser";
import PayWallModalTeams from "./PayWallModalTeams";
import { useTranslation } from "react-i18next";

const DEFAULT_EXPIRY_DAYS = "30";

type Props = {
  signatureConfig: SignatureConfig;
  setSignatureConfig: Dispatch<SetStateAction<SignatureConfig | undefined>>;
  updateSignatureConfig: (updates: any) => void; // TODO: I don't like this, replace this whole "updateSignatureConfig" logic entirely
}

function WorkflowSignatureRequestSidebar(props: Props) {
  const workflowContext = useContext(WorkflowContext)
  const { workflow, mutateWorkflow } = workflowContext;
  const { t } = useTranslation();

  const [redirectUrl, setRedirectUrl] = useState(workflow.signatureConfig?.redirectUrl ?? "");
  const [redirectUrlInvalid, setRedirectUrlInvalid] = useState(false);

  const [expiryDays, setExpiryDays] = useState<string | null>(workflow.signatureConfig?.expiryDuration ? workflow.signatureConfig?.expiryDuration?.split(" ")[0] : '');

  const { signatureConfig, updateSignatureConfig } = props;
  const { openModal } = useModal();
  const { user } = useUser();
  const history = useHistory();
  const { url } = useRouteMatch();

  function handleEditSignatureRecipients() {
    history.push(url + "/document#signable");
  }

  async function updateExpiryDuration(days: string | null) {
    const expiryDuration = days ? `${days} 00:00:00` : null;
    updateSignatureConfig({ expiryDuration });
  }

  async function updateEnableReceiptEmails(enableReceiptEmails: boolean) {
    updateSignatureConfig({ enableReceiptEmails });
  }

  async function updateEnableTracking(enableTracking: boolean) {
    if (!user?.subscription?.featureSignatures) // TODO: Change this to its own feature flag
      return openModal(<PayWallModalPro />);

    updateSignatureConfig({ enableTracking })
  }

  async function updateEnableAuditTrail(enableAuditTrail: boolean) {
    if (!user?.subscription?.featureSignatures) // TODO: Change this to its own feature flag
      return openModal(<PayWallModalPro />);

    updateSignatureConfig({ enableAuditTrail })
  }

  async function updateEnableDraft(autoSend: boolean) {
    updateSignatureConfig({ enableDraftRequests: !autoSend })
  }

  function handleOpenCustomBranding() {
    if (!user?.subscription?.featureCustomBranding)
      return openModal(<PayWallModalTeams />);

    history.push("/settings/branding");
  }

  function handleSaveRedirectUrl() {
    if (!user?.subscription?.featureCustomBranding) {
      setRedirectUrlInvalid(true);
      return openModal(<PayWallModalTeams />);
    }

    updateSignatureConfig({ redirectUrl });
  }

  return (
    <Transition.Child className={`flex flex-col bg-white h-full w-[360px] flex-shrink-0 p-4 pb-24 gap-4 overflow-auto`}
      enter="transition ease-in-out duration-150 transform"
      enterFrom="translate-x-full"
      enterTo="translate-x-0"
      leave="transition ease-in-out duration-150 transform"
      leaveFrom="translate-x-0"
      leaveTo="translate-x-full"
    >

      {!workflow.source && <WorkflowNoSource />}

      {workflow.source && <>
        <WorkflowSourceFieldTable workflow={workflow} mutateWorkflow={mutateWorkflow} />
        <hr />

        {workflow.documents.filter(d => d.isSignable).length === 0 &&
          <div className='flex items-center gap-1 text-yellow font-semibold text-sm bg-yellow/20 border-2 border-yellow p-1 rounded'>
            <ExclamationIcon className='w-8 h-8 flex-shrink-0' /> {t("Please select the documents in this workflow to link to this signature request")}
          </div>
        }

        {workflow.documents.length > 0 &&
          <>
            <div className='flex flex-col gap-2'>
              <div className='flex items-center justify-between'>
                <div className='font-gilroy font-semibold text-gray-600 text-sm'>
                  {t("Signature Request Recipients")}
                </div>
                {workflow.documents.filter(d => d.isSignable).length > 0 &&
                  <div className='text-xs font-gilroy font-semibold text-gray-400 hover:text-blue cursor-pointer' onClick={handleEditSignatureRecipients}>
                    {t("Add/Remove")}
                  </div>
                }
              </div>
              {[...Array(signatureConfig.numRecipients)].map((_, index: number) =>
                <WorkflowSignatureConfigItem key={index} recipientNumber={index + 1} signatureConfig={signatureConfig} updateSignatureConfig={updateSignatureConfig} />
              )}
            </div>

            <div className='flex flex-col gap-1'>
              <div className='text-sm font-semibold font-gilroy text-gray-600 flex gap-2'>
                {t('Auto-send signature requests')}
              </div>
              <div className='flex items-start gap-2'>
                <ToggleButton value={!signatureConfig.enableDraftRequests} onChange={updateEnableDraft} />
                <div className='text-xs font-semibold text-gray-400'>
                  {t('When disabled a draft will be created to be manually sent instead.')}
                </div>
              </div>
            </div>

            <div className='flex flex-col gap-1'>
              <div className='text-sm font-semibold font-gilroy text-gray-600'>
                {t("Send Receipt Emails")}
              </div>
              <div className='flex items-start gap-2'>
                <ToggleButton value={signatureConfig.enableReceiptEmails} onChange={updateEnableReceiptEmails} />
                <div className='text-xs font-semibold text-gray-400 flex-shrink'>
                  {t("Send a copy of the current document immediately after it has been signed by each recipient.")}
                </div>
                <ProLabel />
              </div>
            </div>

            <div className='flex flex-col gap-1'>
              <div className='text-sm font-semibold font-gilroy text-gray-600'>
                {t("Email Tracking")}
              </div>
              <div className='flex items-start gap-2'>
                <ToggleButton value={signatureConfig.enableTracking} onChange={updateEnableTracking} />
                <div className='text-xs font-semibold text-gray-400 flex-shrink'>
                  {t("View when each signature request email is opened")}
                </div>
                <ProLabel />
              </div>
            </div>

            <div className='flex flex-col gap-1'>
              <div className='text-sm font-semibold font-gilroy text-gray-600 flex gap-2'>
                {t("Audit Trail")}
              </div>
              <div className='flex items-start gap-2'>
                <ToggleButton value={signatureConfig.enableAuditTrail} onChange={updateEnableAuditTrail} />
                <div className='text-xs font-semibold text-gray-400 flex-shrink'>
                  {t("Attach an audit trail to the signed document to show the document history")}
                </div>
                <ProLabel />
              </div>
            </div>

            <div className='flex flex-col gap-1'>
              <div className='flex items-center gap-2'>
                <div className='text-sm font-semibold font-gilroy text-gray-600'>
                  {t("Expire Signature Requests")}
                </div>
              </div>
              <div className='flex items-start gap-2'>
                <ToggleButton value={!!signatureConfig.expiryDuration} onChange={enabled => updateExpiryDuration(enabled ? DEFAULT_EXPIRY_DAYS : null)} />
                <div className='flex flex-col'>
                  <div className='text-xs font-semibold text-gray-400'>
                    {t("Have signature requests expire after")}<br /> {t("set amount of days from being sent")}
                  </div>
                  {!!signatureConfig.expiryDuration &&
                    <SmallTextInputField className='w-20 mt-1' value={expiryDays || ""} onChange={value => setExpiryDays(value || "")} onBlur={() => updateExpiryDuration(expiryDays || DEFAULT_EXPIRY_DAYS)} label='Days' placeholder={DEFAULT_EXPIRY_DAYS} number />
                  }
                </div>
              </div>
            </div>

            <div className='flex flex-col'>
              <div className='font-gilroy font-semibold text-gray-600 text-sm'>
                {t("Date Field Format")}
              </div>
              <div className='text-xs font-semibold text-gray-400'>
                {t("Control the format in which the automatic date is added to signed documents when using a date field")}
              </div>
              <div className="flex gap-2 mt-1">
                <SmallSelectField className='flex-1' label={t("Date Format")} value={signatureConfig.dateFormat} onChange={(dateFormat: string) => updateSignatureConfig({ dateFormat })}>
                  {Object.entries(DATE_FORMAT_MAP)
                    .map(([value, label]) => {
                      if (value === workflow.owner?.dateFormat) // Default
                        return <option key={value} value={""}>{label} {t("(Default)")}</option>;

                      return <option key={value} value={value}>{label}</option>;
                    })}
                </SmallSelectField>
                <SmallSelectField className="flex-1" label={t("Time Format")} value={signatureConfig.dateTimeFormat} onChange={(dateTimeFormat: string) => updateSignatureConfig({ dateTimeFormat })}>
                  {Object.entries(TIME_FORMAT_MAP)
                    .map(([value, label]) => {
                      if (value === workflow.owner?.timeFormat) // Default
                        return <option key={value} value={""}>{label} {t("(Default)")}</option>;

                      return <option key={value} value={value}>{label}</option>;
                    })}
                </SmallSelectField>
              </div>
            </div>

            <div className='flex flex-col gap-1'>
              <div className='flex items-center gap-2'>
                <div className='text-sm font-semibold font-gilroy text-gray-600'>
                  {t("Custom Confirmation Page")}
                </div>
                <TeamsLabel />
              </div>
              <div className='text-xs font-semibold text-gray-400'>
                {t("When a recipient has completed a signature request redirect them automatically to your own webpage")}
              </div>
              <SmallTextInputField value={redirectUrl} onChange={value => { setRedirectUrl(value); setRedirectUrlInvalid(false); }} onEnter={handleSaveRedirectUrl} label='URL' placeholder='https://www.example.com/thankyou' invalid={redirectUrlInvalid} />
            </div>

            <div className='flex flex-col gap-1'>
              <div className='flex items-center gap-2'>
                <div className='text-sm font-semibold font-gilroy text-gray-600'>
                  {t("Custom eSign Portal Branding")}
                </div>
                <TeamsLabel />
              </div>
              <div className='text-xs font-semibold text-gray-400'>
                {t("Control the appearance of the eSign portal recipients will visit when to complete your signature request")}
              </div>
              <button className='text-sm btn btn-white py-1' onClick={handleOpenCustomBranding} >{t("Customise Branding")}</button>
            </div>
          </>
        }

        {workflow.documents.length === 0 && <>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1">
              <div className="flex items-center font-gilroy font-semibold text-gray-600">
                <DocumentTextIcon className="w-6 h-6 mr-1" /> {t("Add document")}
              </div>
              <div className="font-semibold text-xs text-gray-400">
                {t("Add a templated document to be included with this email")}
              </div>
            </div>
            <button className="btn btn-white w-40 text-xs" onClick={() => openModal(<WorkflowSelectModal title={t("Add document")} subtitle={t("Add a templated document to be included with this email")} showDocuments {...workflowContext} />)}>
              {t("Add")}
            </button>
          </div>
        </>}

      </>}
    </Transition.Child>
  );
}

export default WorkflowSignatureRequestSidebar;
